import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// Firestore se importa dinámicamente para no traerse el módulo al bundle inicial.
// Este hook se usa en componentes decorativos (SnowEffect, ChristmasGarland) que
// están lazy-loadeados, por lo que la primera evaluación corre tras el mount.

export function useChristmasMode() {
  const { currentUser } = useAuth();
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [userDisabled, setUserDisabled] = useState(false);

  // Estado global del modo navideño (settings/app)
  useEffect(() => {
    let mounted = true;
    let unsubscribe = () => {};

    (async () => {
      const [{ doc, onSnapshot }, { loadFirestore }] = await Promise.all([
        import('firebase/firestore'),
        import('../services/firebase'),
      ]);
      const db = await loadFirestore();
      if (!mounted) return;

      const settingsRef = doc(db, 'settings', 'app');
      unsubscribe = onSnapshot(
        settingsRef,
        (docSnap) => {
          if (!mounted) return;
          setGlobalEnabled(docSnap.exists() ? docSnap.data().isSnowEffectEnabled || false : false);
        },
        (error) => {
          console.error('Error al escuchar settings:', error);
          if (mounted) setGlobalEnabled(false);
        }
      );
    })().catch((err) => {
      console.error('Error inicializando useChristmasMode (settings):', err);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Preferencia personal del usuario (profiles/{uid}.christmasModeDisabled)
  useEffect(() => {
    if (!currentUser) {
      setUserDisabled(false);
      return;
    }

    let mounted = true;
    let unsubscribe = () => {};

    (async () => {
      const [{ doc, onSnapshot }, { loadFirestore }] = await Promise.all([
        import('firebase/firestore'),
        import('../services/firebase'),
      ]);
      const db = await loadFirestore();
      if (!mounted) return;

      const profileRef = doc(db, 'profiles', currentUser.uid);
      unsubscribe = onSnapshot(
        profileRef,
        (docSnap) => {
          if (!mounted) return;
          setUserDisabled(docSnap.exists() ? docSnap.data().christmasModeDisabled || false : false);
        },
        (error) => {
          console.error('Error al escuchar perfil:', error);
          if (mounted) setUserDisabled(false);
        }
      );
    })().catch((err) => {
      console.error('Error inicializando useChristmasMode (profile):', err);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [currentUser]);

  const isChristmasMode = globalEnabled && !userDisabled;

  return {
    isChristmasMode,
    globalEnabled,
    userDisabled,
  };
}
